import React, { ReactElement, useState } from 'react';
import {
  GetScheduleDetailsDocument,
  useUpdateScheduleShiftMutation,
} from 'apollo/generated/client-operations';
import { useIsMounted } from 'utils';
import { FormOnSubmit, ModalForm } from 'components/form';
import {
  ScheduleShift,
  JobsiteScheduleShiftFormFields,
} from 'containers/jobsite/tabs/jobsiteSchedules/data/types';
import { AlertService } from 'components/alertService';
import { getGraphQLError } from 'utils/error';
import {
  getDefaultValues,
  getFormInputs,
  getUpdateInput,
} from './EditShiftModal.forms';

export function EditShiftModal(props: {
  isOpen: boolean;
  closeModal: () => void;
  onConfirm?: () => void;
  shift?: ScheduleShift;
}): ReactElement {
  const { isOpen, closeModal, shift } = props;

  const isMounted = useIsMounted();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const [updateScheduleShift] = useUpdateScheduleShiftMutation({
    refetchQueries: [GetScheduleDetailsDocument],
  });

  const onSubmit: FormOnSubmit<JobsiteScheduleShiftFormFields> = async (
    data,
    event,
    dirtyFields,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const input = getUpdateInput(shift.id, data, dirtyFields);
      await updateScheduleShift({ variables: { input } });
      closeModal();
      AlertService.alert('success', 'Success', 'Shift successfully updated');
    } catch (error) {
      event.preventDefault();
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) {
        setIsSaving(false);
      }
    }
  };

  const inputs = React.useMemo(() => getFormInputs(), []);
  const defaultValues = React.useMemo(() => getDefaultValues(shift), [shift]);

  return (
    <ModalForm
      actionText="Save"
      defaultValues={defaultValues}
      inputs={inputs}
      inputsContainerClassName="odin-grid odin-grid-cols-12 odin-gap-4"
      onSubmit={onSubmit}
      open={isOpen}
      setOpen={closeModal}
      title="Edit shift settings"
    />
  );
}
